import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  type DialogProps,
  type SlideProps
} from '@mui/material';
import React, { useCallback, useState } from 'react';

interface CustomModalProps extends Omit<DialogProps, 'open' | 'onClose'> {
  open?: boolean;
  onClose?: () => void;
  triggerButton?: React.ReactElement;
  title?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  backdropOpacity?: number;
  maxWidth?:any;
  dialogTitle?:React.ReactNode
}

const CustomModal = React.forwardRef<HTMLDivElement, CustomModalProps>(
  (
    {
      open: propOpen,
      onClose: propOnClose,
      triggerButton,
      title,
      children,
      actions,
      backdropOpacity = 0.5,
      maxWidth = 'sm',
      fullWidth = true,
      sx,
      dialogTitle,
      ...dialogProps
    },
    ref
  ) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = propOpen !== undefined;
    const open = isControlled ? propOpen : internalOpen;

    const handleOpen = useCallback(() => {
      if (!isControlled) {
        setInternalOpen(true);
      }
    }, [isControlled]);

    const handleClose = useCallback(() => {
      if (!isControlled) {
        setInternalOpen(false);
      }
      propOnClose?.();
    }, [isControlled, propOnClose]);

    const Transition = React.useMemo(
      () =>
        React.forwardRef(function Transition(
          props: SlideProps & { children: React.ReactElement },
          ref: React.Ref<unknown>
        ) {
          return <Slide direction="up" ref={ref} {...props} />;
        }),
      []
    );

    return (
      <>
        {triggerButton &&
          React.cloneElement(triggerButton, {
            onClick: (e: React.MouseEvent) => {
              triggerButton.props.onClick?.(e);
              handleOpen();
            },
          })}

        <Dialog
          ref={ref}
          open={open}
          onClose={handleClose}
          TransitionComponent={Transition}
          maxWidth={maxWidth}
          fullWidth={fullWidth}
          sx={{
            '& .MuiBackdrop-root': {
              backgroundColor: `rgba(94, 93, 93, 0.37)`,
            },
            '& .MuiPaper-root': {
              boxShadow: '0px 0px 0px rgba(97, 97, 97, 0.1)',
              overflow: 'hidden',
            },
            ...sx,
          }}
          {...dialogProps}
        >
          <DialogTitle sx={{
            backgroundColor: (theme) => theme.palette.background.paper,
            borderBottom: !dialogTitle ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
            padding: !dialogTitle ?'16px' : '16px 24px',
          }}>
            {dialogTitle ? dialogTitle : title}
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            border: 'none',
            padding:2
          }}>
            {children}
          </DialogContent>

          {actions && <DialogActions sx={{
            backgroundColor: (theme) => theme.palette.background.paper,
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            padding: '16px 24px',
          }}>
            {actions}
          </DialogActions>}
        </Dialog>
      </>
    );
  }
);

export default CustomModal;